import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Info } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

import {
  fetchMonetizationJourneyBoard,
  type ExperimentAssignment,
} from "../experiments/experimentClient";
import { MonetizationContactQuickViewModal } from "./MonetizationContactQuickViewModal";
import { MonetizationJourneyColumn } from "./MonetizationJourneyColumn";
import { monetizationQueryKeys } from "./monetizationQueryKeys";

export function MonetizationJourneyBoard({
  experimentId,
  variant,
}: {
  experimentId: string;
  /** Restrict the board to one variant; null shows every assignment. */
  variant: string | null;
}) {
  const [selected, setSelected] = useState<ExperimentAssignment | null>(null);
  const { data, isPending, error } = useQuery({
    queryKey: [...monetizationQueryKeys.board(experimentId), variant ?? "all"],
    queryFn: () => fetchMonetizationJourneyBoard({ experimentId, variant }),
    staleTime: 30_000,
  });

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>Monetization journeys are unavailable</AlertTitle>
        <AlertDescription>{String(error)}</AlertDescription>
      </Alert>
    );
  }
  if (isPending) return <BoardSkeleton />;
  if (!data) return null;

  return (
    <div className="space-y-3">
      <Alert>
        <Info />
        <AlertTitle>Outcome definitions</AlertTitle>
        <AlertDescription>
          <strong>Paid</strong> is a durable Stripe success.{" "}
          <strong>Cart abandoned</strong> means the customer opened a checkout
          and never paid, so Stripe expired the link — they get one reminder
          while it is still live. <strong>Payment issue</strong> means the card
          was actually declined. <strong>Offer open</strong> means no purchase
          yet; silence is not labeled as a rejection. Open any contact to
          inspect their exact commerce events, Echoes, balance, and WhatsApp
          conversation.
        </AlertDescription>
      </Alert>

      {data.truncated ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Partial result</AlertTitle>
          <AlertDescription>
            The experiment exceeded the API safety limit. Do not make a rollout
            decision from this partial board.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex gap-3 overflow-x-auto pb-4">
        {data.columns.map((column) => (
          <MonetizationJourneyColumn
            key={`${experimentId}:${column.key}:${column.total}:${column.assignments[0]?.latest_event_at ?? ""}`}
            experimentId={experimentId}
            variant={variant}
            column={column}
            onSelect={setSelected}
          />
        ))}
      </div>

      <MonetizationContactQuickViewModal
        assignment={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="flex gap-3 overflow-x-auto pb-4">
      {Array.from({ length: 6 }).map((_, columnIndex) => (
        <div
          key={columnIndex}
          className="flex w-72 shrink-0 flex-col gap-2 rounded-lg border bg-muted/20 p-2"
        >
          <Skeleton className="h-7 w-full" />
          {Array.from({ length: 3 }).map((__, cardIndex) => (
            <Skeleton key={cardIndex} className="h-24 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}
